(() => {
  'use strict';

  const STORAGE_KEY = 'atemp_wishlist_v1';
  const VERSION = 1;
  const BUTTON_SELECTOR = '[data-atemp-wishlist-button]';
  const CHANGE_EVENT = 'atemp:wishlist:change';

  if (window.ATEMPWishlist) {
    window.ATEMPWishlist.sync();
    return;
  }

  const state = {
    items: [],
    observer: null,
    syncFrame: 0,
  };

  const normalizeItem = (item) => {
    if (!item || item.id == null || !item.handle) return null;

    return {
      id: String(item.id),
      handle: String(item.handle),
      title: String(item.title || ''),
      url: String(item.url || `/products/${item.handle}`),
      image: String(item.image || ''),
      addedAt: Number(item.addedAt) || Date.now(),
    };
  };

  const uniqueItems = (items) => {
    const map = new Map();

    for (const rawItem of Array.isArray(items) ? items : []) {
      const item = normalizeItem(rawItem);
      if (item) map.set(item.id, item);
    }

    return [...map.values()];
  };

  const readStorage = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) {
        return uniqueItems(parsed);
      }

      return uniqueItems(parsed?.items);
    } catch (error) {
      console.warn('[ATEMP Wishlist] Não foi possível ler os favoritos.', error);
      return [];
    }
  };

  const writeStorage = () => {
    const payload = {
      version: VERSION,
      items: state.items,
    };

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      return true;
    } catch (error) {
      console.warn('[ATEMP Wishlist] Não foi possível salvar os favoritos.', error);
      return false;
    }
  };

  const has = (productId) =>
    state.items.some((item) => item.id === String(productId));

  const getButtonItem = (button) =>
    normalizeItem({
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      addedAt: Date.now(),
    });

  const updateButton = (button) => {
    const active = has(button.dataset.productId);

    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', String(active));
    button.dataset.favoriteState = active ? 'active' : 'inactive';
    button.title = active ? 'Remover dos favoritos' : 'Adicionar aos favoritos';
  };

  const sync = (root = document) => {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll(BUTTON_SELECTOR).forEach(updateButton);
    document.documentElement.dataset.atempWishlistCount = String(state.items.length);
  };

  const scheduleSync = () => {
    if (state.syncFrame) return;

    state.syncFrame = window.requestAnimationFrame(() => {
      state.syncFrame = 0;
      sync();
    });
  };

  const getLiveRegion = () => {
    let region = document.getElementById('atemp-wishlist-live-region');

    if (!region) {
      region = document.createElement('div');
      region.id = 'atemp-wishlist-live-region';
      region.className = 'visually-hidden';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
    }

    return region;
  };

  const announce = (message) => {
    const region = getLiveRegion();
    region.textContent = '';

    window.setTimeout(() => {
      region.textContent = message;
    }, 30);
  };

  const emitChange = () => {
    const detail = {
      count: state.items.length,
      items: [...state.items],
    };

    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail }));
  };

  const commit = (message) => {
    writeStorage();
    sync();
    emitChange();

    if (message) announce(message);
  };

  const add = (rawItem) => {
    const item = normalizeItem(rawItem);
    if (!item || has(item.id)) return false;

    state.items = [...state.items, item];
    commit(`${item.title || 'Produto'} adicionado aos favoritos.`);
    return true;
  };

  const remove = (productId) => {
    const id = String(productId);
    const item = state.items.find((candidate) => candidate.id === id);
    const nextItems = state.items.filter((candidate) => candidate.id !== id);

    if (nextItems.length === state.items.length) return false;

    state.items = nextItems;
    commit(`${item?.title || 'Produto'} removido dos favoritos.`);
    return true;
  };

  const toggle = (rawItem) => {
    const item = normalizeItem(rawItem);
    if (!item) return false;

    return has(item.id) ? remove(item.id) : add(item);
  };

  const handleWishlistClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest(BUTTON_SELECTOR);
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    toggle(getButtonItem(button));
  };

  const stopCardPointerHandling = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest(BUTTON_SELECTOR)) {
      event.stopPropagation();
    }
  };

  const handleStorage = (event) => {
    if (event.key !== STORAGE_KEY) return;

    state.items = readStorage();
    sync();
    emitChange();
  };

  const handleSectionLoad = (event) => {
    sync(event.target instanceof Element ? event.target : document);
  };

  const startObserver = () => {
    if (state.observer || !document.documentElement) return;

    state.observer = new MutationObserver((mutations) => {
      const hasWishlistNodes = mutations.some((mutation) =>
        [...mutation.addedNodes].some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches(BUTTON_SELECTOR) || node.querySelector(BUTTON_SELECTOR);
        })
      );

      if (hasWishlistNodes) scheduleSync();
    });

    state.observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  const init = () => {
    state.items = readStorage();
    sync();
    startObserver();
    emitChange();
  };

  document.addEventListener('click', handleWishlistClick, true);
  document.addEventListener('pointerdown', stopCardPointerHandling, true);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  window.addEventListener('storage', handleStorage);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.ATEMPWishlist = Object.freeze({
    key: STORAGE_KEY,
    getItems: () => [...state.items],
    count: () => state.items.length,
    has,
    add,
    remove,
    toggle,
    sync,
  });
})();